import {
  Box,
  Pagination,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import EmptyData from "../Common/EmptyData";

const ROWS_OPTIONS = [5, 10, 25, 50];

/**
 * Product list table with MUI Pagination + rows-per-page (server-driven totals).
 */
const ProductTableWithPagination = ({
  data = [],
  loading = false,
  pagination = {},
  page = 1,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const totalItems =
    Number(
      pagination.totalItems ?? pagination.totalCount ?? pagination.count ?? 0,
    ) || 0;

  const start = totalItems === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, totalItems);

  const handleRowClick = (id) => {
    navigate(`/dashboard/product-details/${id}`);
  };

  const showEmpty = !loading && (!data || data.length === 0);

  return (
    <Box sx={{ mt: 2 }}>
      {showEmpty ? (
        <EmptyData />
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f8f7" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Product ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Origin</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Price($)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item) => (
                  <TableRow
                    key={item?._id}
                    hover
                    onClick={() => handleRowClick(item?._id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{item?._id}</TableCell>
                    <TableCell>{item?.name}</TableCell>
                    <TableCell>{item?.categories?.[0]?.name}</TableCell>
                    <TableCell>{item?.country?.name}</TableCell>
                    <TableCell>
                      {item?.price != null
                        ? Number(item.price).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          bgcolor:
                            item?.status === "APPROVED"
                              ? "#ECF8F0"
                              : item?.status === "DEACTIVATED"
                                ? "#F0F0F0"
                                : "#FFE5E8",
                          color:
                            item?.status === "APPROVED"
                              ? "#3FB763"
                              : item?.status === "DEACTIVATED"
                                ? "#666"
                                : "#FF3347",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 500,
                          textAlign: "center",
                        }}
                      >
                        {item?.status === "APPROVED"
                          ? "Active"
                          : item?.status === "DEACTIVATED"
                            ? "Deactivated"
                            : "Pending"}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              px: 1,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#424242" }}>
              {totalItems === 0
                ? "0 results"
                : `${start}–${end} of ${totalItems} results`}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#424242",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Rows per page
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) =>
                  onRowsPerPageChange?.(Number(e.target.value))
                }
                sx={{ minWidth: 80, fontSize: "13px" }}
              >
                {ROWS_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, newPage) => {
                  if (
                    typeof newPage === "number" &&
                    Number.isFinite(newPage) &&
                    newPage >= 1
                  ) {
                    onPageChange?.(event, newPage);
                  }
                }}
                color="primary"
                size="small"
                disabled={loading}
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProductTableWithPagination;
